import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber,
  message,
  Switch,
  Space, 
  Tag
} from 'antd';
import { Pack, packService, getPackPrice, getPackCurrencySymbol, convertDataValue } from '../../services/packService';
import TableFilter, { FilterConfig } from '../../components/TableFilter/TableFilter';
import { useOptimizedPageSearch } from '../../hooks/useOptimizedPageSearch';
import ReactCountryFlag from 'react-country-flag';
import getCountryISO2 from 'country-iso-3-to-2';
import PackFilters from 'components/PackFilters/PackFilters';

const Packs: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [packs, setPacks] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState<any>({});
  const [numberRangeFilters, setNumberRangeFilters] = useState<{
    prices?: [number | undefined, number | undefined];
    dataValue?: [number | undefined, number | undefined];
    validityDuration?: [number | undefined, number | undefined];
  }>({});


  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);




// Remplacez vos deux useEffect actuels par celui-ci
useEffect(() => {
  fetchPacks(
    pagination.current, 
    pagination.pageSize, 
    { ...filters, ...numberRangeFilters },
    sortBy,
    sortOrder
  );
}, [pagination.current, pagination.pageSize, filters, numberRangeFilters, sortBy, sortOrder]);

  // useEffect(() => {
  //   fetchPacks(pagination.current, pagination.pageSize, {
  //     ...filters,
  //     ...numberRangeFilters,
  //   });
  // }, [pagination.current, pagination.pageSize, filters, numberRangeFilters]);

  const mapFiltersToBackend = (filters: any) => {
    return {
      name: filters.name,
      status: filters.status,
      priceMin: filters.prices?.[0],
      priceMax: filters.prices?.[1],
      dataMin: filters.dataValue?.[0] ? filters.dataValue[0] * 1024 * 1024 : undefined, 
      dataMax: filters.dataValue?.[1] ? filters.dataValue[1] * 1024 * 1024 : undefined, 
      validityMin: filters.validityDuration?.[0],
      validityMax: filters.validityDuration?.[1],
    };
  };


//   const fetchPacks = async (
//   page = 1,
//   pageSize = 10,
//   appliedFilters = filters,
//   sortField?: string,
//   sortDirection?: 'asc' | 'desc'
// ) => {
//   try {
//     setLoading(true);
//   console.error('Filtres envoyés au backend:', mapFiltersToBackend(appliedFilters));
//     const response = await packService.getAllAdmin({
//       page: page - 1,
//       size: pageSize,
//       filters: mapFiltersToBackend(appliedFilters),
//       sortBy: sortField,
//       sortOrder: sortDirection,
//     });

//     setPacks(response.data.content);
//     setPagination({
//       current: page,
//       pageSize,
//       total: response.data.totalElements,
//     });
//   } catch {
//     message.error('Erreur lors du chargement des packs');
//   } finally {
//     setLoading(false);
//   }
// };


  const fetchPacks = async (
      page = 1,
      pageSize = 10,
      appliedFilters = filters,
      sortField?: string,
      sortDirection?: 'asc' | 'desc'
    ) => {
      try {
        setLoading(true);

        // transformer sortBy + sortOrder en sort compatible Spring
        const sortParam = sortField ? `${sortField},${sortDirection ?? 'asc'}` : undefined;

        console.error('Filtres envoyés au backend:', mapFiltersToBackend(appliedFilters));
        const response = await packService.getAllAdmin({
          page: page - 1,
          size: pageSize,
          filters: mapFiltersToBackend(appliedFilters),
          sort: sortParam, // <-- ici on envoie le sort correct
        });

        setPacks(response.data.content);
        setPagination({
          current: page,
          pageSize,
          total: response.data.totalElements,
        });
      } catch {
        message.error(t('admin.packs.loadError'));
      } finally {
        setLoading(false);
      }
    };


  const handleSubmit = async (values: any) => {
    try {
      // Transformer les données du formulaire vers la structure Pack
      const packData: Pack = {
        id: editingId || 0,
        productId: values.productId,
        name: values.name,
        description: values.description,
        prices: [{
          id: 0,
          amount: values.price,
          sabilyAmount: values.price,
          agencyAmount: values.agencyPrice,
          currency: { code: 'EUR', symbol: '€' }
        }],
        validityDuration: values.duration,
        validityDurationUnit: 'days',
        dataValue: values.dataLimit,
        dataUnit: 'GB',
        unlimited: false,
        status: values.isActive ? 'available' : 'UNAVAILABLE',
        isActive: values.isActive,
        countries: values.countries || []
      };

      if (editingId) {
        // Mise à jour d'un pack existant
        await packService.update(editingId, packData);
        message.success(t('admin.packs.statusChangeSuccess'));
      } else {
        // Création d'un nouveau pack
        await packService.create(packData);
        message.success(t('admin.packs.creationSuccess'));
      }
      
      // Fermer la modal, réinitialiser le formulaire et rafraîchir la liste
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchPacks();
    } catch (error) {
      const action = editingId ? 'la modification' : 'la création';
      message.error(t('admin.packs.saveError'));
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingId(null);
  };


  
    const renderVerificationTag = (status: string) => {
      const statusMap: Record<string, { color: string; text: string }> = {
        available: { color: 'green', text: 'Actif' },
        UNAVAILABLE: { color: 'orange', text: 'Inactif' },
      };

  return (
    <Tag color={statusMap[status]?.color || 'default'}>
      {statusMap[status]?.text || status}
    </Tag>
  );
}

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortDirections: ['ascend' as const, 'descend' as const],
   render: (name: string, record: Pack) => (
    <>
      <div
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          fontWeight: 500,
        }}
      >
        {name}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',   // ✅ saut de ligne automatique
          gap: '4px',
          marginTop: '4px',
        }}
      >
        {record.countries?.map(country => (
          <ReactCountryFlag
            key={country.code}
            countryCode={getCountryISO2(country.code)}
            svg
            style={{
              width: '1.5em',
              height: '1.5em',
              borderRadius: '50%',
            }}
          />
        ))}
      </div>
    </>
     ),
    },    
    
    {
      title: t('admin.packs.publicPrice'),
      dataIndex: 'prices.sabilyAmount',
      key: 'price',
      sorter:true,
      sortDirections: ['ascend' as const, 'descend' as const],
      render: (prices: any, record: Pack) => `${getPackPrice(record)} ${getPackCurrencySymbol(record)}`
    },    
    {
      title: t('admin.packs.data'),
      dataIndex: 'dataValue',
      key: 'dataValue',
      render: (data: number, record: Pack) => {
        const { value, unit } = convertDataValue(record);
        return `${value} ${unit}`;
      }
    },
    {
      title: t('admin.packs.duration'),
      dataIndex: 'validityDuration',
      key: 'validityDuration',
      render: (duration: number) => t('admin.packs.daysFormat', { count: duration })
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: renderVerificationTag, 
    
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleActivate(record)}
          >
            {record.status === "available" ? t('admin.packs.deactivateAction') : t('admin.packs.activateAction')}
          </Button>
          
        </Space>
      )
    }
  ];


  const handleActivate = async (record: Pack) => {
    try {
      const newStatus = record.status === "available" ? "UNAVAILABLE" : "available";
      await packService.updateStatus(record.id, newStatus );
        message.success(t('admin.packs.statusChangeSuccess'));
        fetchPacks();
    } catch (error) {
      message.error(t('admin.packs.statusChangeError'));
    }
  };


  return (
    <>
      <PackFilters
        filters={filters}
        numberRangeFilters={numberRangeFilters}
        setFilters={setFilters}
        setNumberRangeFilters={setNumberRangeFilters}
        onClearAll={() => {
          setFilters({});
          setNumberRangeFilters({});
        }}
      />

      <Card 
        title={`${t('admin.packs.cardTitle')} (${pagination.total})`}
        extra={
          <Space>
            <Button onClick={() => window.location.href = '/admin/update-packs-prices'}>
              {t('admin.packs.updatePricesBtn')}
            </Button>
            
          </Space>
        }
      >
   <Table

        columns={columns.map(col => {
          if (!col.sorter) {
            return col; // ✅ aucune flèche
          }

          let backendKey = col.key;
          if (col.key === 'price') backendKey = 'prices.sabilyAmount';

          return {
            ...col,
            sortOrder:
              sortBy === backendKey
                ? sortOrder === 'asc'
                  ? 'ascend'
                  : 'descend'
                : undefined,
          };
        })}
        dataSource={packs}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} packs`,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
      onChange={(pag, _filters, sorter: any) => {
        // Mapper le champ frontend vers backend
        let field = sorter.field;
        if (field === 'price') field = 'prices.sabilyAmount';
        if (field === 'name') field = 'name';

        console.error("prices.sabilyAmount",field);
    

        let order: 'asc' | 'desc' = 'asc'; // valeur par défaut
        if (sorter.order === 'descend') order = 'desc';

        const isPageSizeChange = pag.pageSize !== pagination.pageSize;

        setSortBy(field);
        setSortOrder(order);
        setPagination(prev => ({
          ...prev,
          current: isPageSizeChange ? 1 : pag.current || 1,
          pageSize: pag.pageSize || prev.pageSize,
        }));

        fetchPacks(
          isPageSizeChange ? 1 : pag.current || 1,
          pag.pageSize || pagination.pageSize,
          { ...filters, ...numberRangeFilters },
          field,
          order
        );
      }}

/>



      </Card>

      <Modal
        title={editingId ? t('admin.packs.editTitle') : t('admin.packs.createTitle')}
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('admin.packs.nameRequired') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="price"
            label={`${t('common.amount')} (€)`}
            rules={[{ required: true, message: t('admin.packs.priceRequired') }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="dataLimit"
            label={t('admin.packs.dataLimit')}
            rules={[{ required: true, message: t('admin.packs.dataLimitRequired') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="duration"
            label={t('admin.packs.durationDays')}
            rules={[{ required: true, message: t('admin.packs.durationRequired') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="isActive"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {editingId ? t('common.edit') : t('common.create')}
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleCloseModal}>
              {t('common.cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Packs;
